import {
  PermissionChange,
  Subscription,
  DeregisterDevice,
  RegisterDevice
} from "./../models/notifications.model";
import { PushNotificationService } from './../services/common/services-push-notifications';
import { NotificationService } from './../services/common/services-notifications';
import { State, Selector, Action, StateContext, createSelector } from '@ngxs/store';
import { NotificationStateModel, SubscribeTopic  } from '../models/notifications.model';
import { map, tap, mergeMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { merge } from 'rxjs';
import { produce } from 'immer';
// wrt subscription tracking, what's our goal?
// Options:
// 1. Route-based general subscription, presence-based unsubscription
// 2. Route-based general subscription, auth session scoped (unsubscribe on log out)
// 3. Route based general subscription, with specific id and route prioritization, auth session scoped
//    i.e. having visited the scraper page in the past, all notifications come through (in some form)
//    if present at the scraper /right now/, present a visible notification unconditionally
//    if having registered interest in a specific item that has not yet reached a 'satisfied'
//    condition (dependent on the particular kind of entity), present a visible notification
//    otherwise, show the unobtrusive version (increment the 'n new notifications' badge).

// we need to keep tabs on the state of the service worker, specifically its registration, and its push manager status
@State<NotificationStateModel>({
  name: "push_notifications",
  defaults: {
    subscriptionHash: null,
    subscriptions: {},
    permission: '',
  }
})
@Injectable()
export class NotificationState {

  @Selector()
  static getSubscriptions(state: NotificationStateModel) {
    return state.subscriptions;
  }

  @Selector()
  static getSubscriptionHash(state: NotificationStateModel) {
    return state.subscriptionHash;
  }

  constructor(public pushService: PushNotificationService, public notificationService: NotificationService) {
  }
  @Action(SubscribeTopic)
  async subscribe({ patchState, setState, dispatch, getState }: StateContext<NotificationStateModel>, { payload }: SubscribeTopic) {
    if(!(await this.pushService.checkSubscription())) {
      // the client has never subscribed, and may not even have a service worker
      const response = await this.pushService.requestPermission();
      if(response === 'granted') {
        await this.pushService.subscribeToTopic(payload).toPromise();
        setState(produce(draft => {
          const match = draft.subscriptions[payload.topic];
          if(match) {
            draft.subscriptions[payload.topic].subscribed = true;
          } else {
            draft.subscriptions[payload.topic] = {subscribed: true, subtopics: {}}
          }
          if (payload.extendedTopic) {
            draft.subscriptions[payload.topic].subtopics[
              payload.extendedTopic
            ] = true;
          }
        }))
      } else if(response === 'default' || response === 'denied') {
        this.notificationService.notify("The notification permission is required for progress events, please enable it", 'error')
      } else if (!response) {
        this.notificationService.notify("Push notifications are disabled on insecure origins (non-https origins other than localhost)", 'error');
      }
    }

  }

  @Action(RegisterDevice)
  async RegisterDevice({patchState, dispatch, getState }: StateContext<NotificationStateModel>, payload: RegisterDevice) {
    if(getState().permission === 'granted') {
      const subscriptionHash = await this.pushService.registerDevice();
      patchState({ subscriptionHash });
    }
  }

  @Action(DeregisterDevice)
  async deregisterDevice({patchState, dispatch, getState }: StateContext<NotificationStateModel>, payload: DeregisterDevice) {
    try {
      await this.pushService.deregisterDevice();
    }
    catch(err) {console.log(err)}
    patchState({subscriptionHash: null, subscriptions: {}})
  }

  @Action(PermissionChange)
  async updatePermission({ patchState, setState, dispatch, getState}: StateContext<NotificationStateModel>, {payload } : PermissionChange) {
    const {permission: current, ...rest} = getState();

    if(payload.state === 'granted') {
      if(!(await this.pushService.checkSubscription())) {
        const subscriptionHash = await this.pushService.registerDevice();
        patchState({subscriptionHash})
      } else {
        // ensure that we don't have a push subscription with no remote equivalent
        if (!(await this.pushService.checkRemoteDeviceRegistration())) {
          await this.pushService.registerDevice();
        }
      }
    } else {
      console.log('checking for existing subscription')
      // for all other states, we MUST ensure that the local service worker has no subscriptions, and the remote has deleted everything
      if(await this.pushService.checkSubscription()) {
        console.log('correcting state de-sync')
        // something in the lifecycle has failed
        await this.pushService.deregisterDevice();
        // web push broker will take care of removing subscriptions

      }
      patchState({ subscriptionHash: null, subscriptions: {} });
    }

    if (payload.state !== current) {
      patchState({ permission: payload.state });
    }
  }
}
